import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  ACTIVITIES,
  ACTIVITY_LABEL,
  ActivityId,
  SessionStatus,
  formatTime,
  Session,
  Org,
  activityFamily,
} from "../mockData";
import { StatusBadge } from "../components/StatusBadge";
import ConfirmModal from "../components/ConfirmModal";
import { RiskBadge } from "../components/RiskBadge";
import { ActivityMultiFilter, OptionFilter, FilterCard, FilterRow, FilterChip, ScoreRange } from "../components/VisualFilter";
import { TierFilterRow, AthleteFilterRow, parseAthletesParam, encodeAthletesParam } from "../components/ListFilters";
import { Download, Search, Loader2, Globe2, Trash2, RefreshCw, Gauge, CircleSlash } from "lucide-react";
import { useApiResource, useApi } from "../api/useApi";
import { listUploads, deleteUpload, requestRescore } from "../api/resources";
import { TIER_LABELS } from "../components/TierBadge";
import { uploadsToSessions, sportIdsForFamily } from "../api/adapters";
import type { IListItem } from "../api/types";

const PAGE_SIZE = 10;

const STATUSES: { value: SessionStatus; label: string; emoji?: string }[] = [
  { value: "processed",  label: "Processed",  emoji: "✅" },
  { value: "processing", label: "Processing", emoji: "⏳" },
  { value: "queued",     label: "Queued",     emoji: "🕒" },
  { value: "failed",     label: "Failed",     emoji: "⚠️" },
];

export default function Sessions() {
  const { org } = useOutletContext<{ org: Org }>();
  const { user } = useAuth0();

  // ALL filter state lives in the URL query string, so leaving the page (or sharing the
  // link) and coming back restores the exact view instead of resetting every filter.
  // Multi-entry filters (sports/tiers/athletes) are comma-joined single params.
  const [searchParams, setSearchParams] = useSearchParams();

  const sports = useMemo(
    () => (searchParams.get("sports") ?? "").split(",").filter(Boolean),
    [searchParams]
  );
  const technique = (searchParams.get("technique") as ActivityId | null) ?? "all";
  const status = (searchParams.get("status") as SessionStatus | "all" | null) ?? "all";
  const q = searchParams.get("q") ?? "";

  // Outcome-score filter — SERVER-side (SQL on the latest score row), because a client-side
  // score filter over the accumulated pages would disagree with totalCount and "Load more".
  // URL form: score=none, or score=<min>-<max>.
  const scoreParam = searchParams.get("score");
  const scoreMode: "all" | "none" | "range" =
    scoreParam === "none" ? "none" : scoreParam ? "range" : "all";
  const [scoreMinRaw, scoreMaxRaw] = (scoreParam && scoreParam !== "none"
    ? scoreParam.split("-") : []).map(Number);
  const scoreMin = Number.isFinite(scoreMinRaw) ? scoreMinRaw : 0;
  const scoreMax = Number.isFinite(scoreMaxRaw) ? scoreMaxRaw : 100;

  // Upload-tag filters — also SERVER-side (same list+count SQL as the score filter), both
  // multi-entry (values within a filter OR together). Tier is open to EVERYONE including
  // Elite Pro: only ASSIGNING that tier is admin-gated. Athletes = who is IN the video
  // (the subjectContactId tag, not the uploader), picked via the same contactsSearch the
  // subject-tagging dialog uses.
  const tiers = useMemo(
    () => (searchParams.get("tiers") ?? "").split(",").filter(Boolean),
    [searchParams]
  );
  const athletes = useMemo(
    () => parseAthletesParam(searchParams.get("athletes")),
    [searchParams]
  );

  // Server-side pagination with client-side accumulation: fetch a page at a time and append, so
  // "Load more" grows the list without re-fetching everything.
  const [page, setPage] = useState(1);
  const [allUploads, setAllUploads] = useState<IListItem[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const apiFetch = useApi();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Asked and reported IN THE PAGE: embedded browsers (the app's own among them) swallow window.confirm / alert, so
  // Delete silently did nothing (owner, 2026-10-15).
  const [deleteAsk, setDeleteAsk] = useState<Session | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Re-scoring (rescore-triggers): checkbox selection for the bulk action, plus the set of
  // rows optimistically marked queued after a successful request (server-known pending rows
  // come in on the read model as rescorePending).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [queuedIds, setQueuedIds] = useState<Set<string>>(new Set());
  const [rescoring, setRescoring] = useState(false);

  // Specific techniques/angles — only meaningful within exactly ONE chosen sport family.
  const techniqueOptions = useMemo(
    () => (sports.length === 1 ? ACTIVITIES.filter((a) => a.group === sports[0]) : []),
    [sports]
  );

  // Any server-side filter change re-queries from scratch, so reset paging + accumulation.
  const resetPaging = () => {
    setPage(1);
    setAllUploads([]);
    setTotalCount(null);
  };

  // Single writer for the URL-persisted filter state. replace (not push) so slider drags and
  // chip toggles don't bury the history stack; the URL still restores on back/return.
  const updateParams = (mutate: (p: URLSearchParams) => void, resetsServerQuery = true) => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    setSearchParams(next, { replace: true });
    if (resetsServerQuery) resetPaging();
  };

  const setListParam = (key: string, value: string | null, resetsServerQuery = true) =>
    updateParams((p) => {
      if (value == null || value === "" || value === "all") p.delete(key);
      else p.set(key, value);
    }, resetsServerQuery);

  // Toggling a sport family re-queries the server, so reset paging + accumulation. The
  // technique sub-filter only makes sense within a single family — drop it when the
  // selection is no longer exactly its family.
  const toggleSport = (nextSports: string[]) =>
    updateParams((p) => {
      if (nextSports.length === 0) p.delete("sports");
      else p.set("sports", nextSports.join(","));
      const tech = p.get("technique");
      if (tech && (nextSports.length !== 1 ||
          ACTIVITIES.find((a) => a.id === tech)?.group !== nextSports[0]))
        p.delete("technique");
    });

  const setScoreModeAndReset = (next: "all" | "none" | "range") =>
    setListParam("score", next === "all" ? null : next === "none" ? "none" : `${scoreMin}-${scoreMax}`);

  const setScoreRangeAndReset = (lo: number, hi: number) =>
    setListParam("score", `${lo}-${hi}`);

  const toggleTier = (tier: string) => {
    const next = tiers.includes(tier) ? tiers.filter((t) => t !== tier) : [...tiers, tier];
    setListParam("tiers", next.length > 0 ? next.join(",") : null);
  };

  const clearTiers = () => setListParam("tiers", null);

  const setAthletesAndReset = (next: { id: number; name: string }[]) =>
    setListParam("athletes", next.length > 0 ? encodeAthletesParam(next) : null);

  const addAthlete = (a: { id: number; name: string }) => {
    if (!athletes.some((x) => x.id === a.id)) setAthletesAndReset([...athletes, a]);
    else setAthletesAndReset(athletes);
  };

  const removeAthlete = (id: number) =>
    setAthletesAndReset(athletes.filter((a) => a.id !== id));

  // Sport families -> union of backend SportIds (server-side filter); none = no sport filter.
  const sportIds = useMemo(
    () => (sports.length === 0 ? undefined : sports.flatMap(sportIdsForFamily)),
    [sports]
  );

  const athleteIdsKey = athletes.map((a) => a.id).join(",");
  const { data: uploadsResp, isLoading, error, reload } = useApiResource(
    (apiFetch) =>
      listUploads(apiFetch, {
        page,
        pageSize: PAGE_SIZE,
        sportIds,
        noScore: scoreMode === "none" || undefined,
        scoreMin: scoreMode === "range" ? scoreMin : undefined,
        scoreMax: scoreMode === "range" ? scoreMax : undefined,
        tiers: tiers.length > 0 ? tiers : undefined,
        subjectContactIds: athletes.length > 0 ? athletes.map((a) => a.id) : undefined,
      }),
    [page, sports.join(","), scoreMode, scoreMin, scoreMax, tiers.join(","), athleteIdsKey]
  );

  // Merge each loaded page into the accumulator, deduping by uploadId so a re-fired
  // effect (or a reload of the same page) can't add the same upload twice.
  useEffect(() => {
    if (!uploadsResp) return;
    const vals = uploadsResp.values ?? [];
    setAllUploads((prev) => {
      if (page === 1) return vals;
      const seen = new Set(prev.map((u) => u.uploadId));
      return [...prev, ...vals.filter((v) => !seen.has(v.uploadId))];
    });
    if (uploadsResp.totalCount != null) setTotalCount(uploadsResp.totalCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadsResp]);

  const sessions: Session[] = useMemo(
    () =>
      uploadsToSessions(allUploads, {
        athleteName: user?.name ?? user?.email ?? "You",
        athleteId: user?.sub ?? "self",
      }),
    [allUploads, user]
  );

  const hasMore = totalCount != null && allUploads.length < totalCount;

  // Delete an upload (video + all associated data) via the DeleteUpload endpoint, then drop it
  // from the accumulated list optimistically. Session.id is the stringified numeric upload id.
  const onDelete = useCallback(
    (s: Session) => {
      if (!Number.isFinite(Number(s.id)) || deletingId) return;
      setNotice(null);
      setDeleteAsk(s);
    },
    [deletingId]
  );
  const doDelete = useCallback(
    async (s: Session) => {
      setDeleteAsk(null);
      const idNum = Number(s.id);
      setDeletingId(s.id);
      try {
        await deleteUpload(apiFetch, idNum);
        setAllUploads((prev) => prev.filter((u) => String(u.uploadId ?? u.name) !== s.id));
        setTotalCount((c) => (c != null ? Math.max(0, c - 1) : c));
      } catch {
        setNotice("Couldn't delete this session. Please try again.");
      } finally {
        setDeletingId(null);
      }
    },
    [apiFetch]
  );

  // POST the re-score request for a set of sessions; on success mark exactly those rows
  // queued (optimistic — matches what the next list read will report as rescorePending).
  const onRescore = useCallback(
    async (sessionIds: string[]) => {
      const idNums = sessionIds.map(Number).filter((n) => Number.isFinite(n));
      if (idNums.length === 0 || rescoring) return;
      setRescoring(true);
      try {
        await requestRescore(apiFetch, idNums);
        setQueuedIds((prev) => {
          const next = new Set(prev);
          sessionIds.forEach((id) => next.add(id));
          return next;
        });
        setSelectedIds(new Set());
      } catch {
        setNotice("Couldn't queue the re-score. Please try again.");
      } finally {
        setRescoring(false);
      }
    },
    [apiFetch, rescoring]
  );

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return sessions.filter((s) => {
      if (s.orgId && s.orgId !== org.id) return false;
      if (sports.length > 0 && !sports.includes(activityFamily(s.activity))) return false;
      if (technique !== "all" && s.activity !== technique) return false;
      if (status !== "all" && s.status !== status) return false;
      if (needle) {
        // Mirror EL-App's search (view.videoList.tsx:240) — match name OR upload id.
        const haystack = [s.id, s.notes ?? "", s.athlete, s.task ?? ""]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [sessions, sports, technique, status, q, org.id]);

  const activeCount =
    (sports.length > 0 ? 1 : 0) +
    (technique !== "all" ? 1 : 0) +
    (status !== "all" ? 1 : 0) +
    (scoreMode !== "all" ? 1 : 0) +
    (tiers.length > 0 ? 1 : 0) +
    (athletes.length > 0 ? 1 : 0) +
    (q ? 1 : 0);

  const scoreSummary =
    scoreMode === "none" ? "No score" :
    scoreMode === "range" ? `${scoreMin}–${scoreMax}` : "All";

  const techniqueLabel = (id: ActivityId) =>
    ACTIVITIES.find((a) => a.id === id)?.label.replace(/^[^—]+ — /, "") ?? ACTIVITY_LABEL[id];

  return (
    <div className="space-y-4">
      <ConfirmModal
        open={deleteAsk != null}
        title="Delete this session?"
        message={`Delete session ${deleteAsk?.id ?? ""} and all of its data? This can't be undone.`}
        confirmLabel="Delete"
        onConfirm={() => deleteAsk && void doDelete(deleteAsk)}
        onCancel={() => setDeleteAsk(null)}
      />
      {notice && (
        <div
          className="flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          role="alert"
          data-testid="page-notice"
        >
          <span>{notice}</span>
          <button type="button" className="shrink-0 text-xs underline" onClick={() => setNotice(null)}>
            Dismiss
          </button>
        </div>
      )}
      <FilterCard
        countActive={activeCount}
        rightHint={
          <button className="btn-secondary !h-9 hidden sm:inline-flex">
            <Download className="h-4 w-4" /> Export
          </button>
        }
        search={
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setListParam("q", e.target.value || null, false)}
              placeholder="Search by name or upload id…"
              className="w-full h-9 pl-9 pr-3 text-sm rounded-md bg-white border border-slate-200 focus:outline-none focus:shadow-ring"
            />
          </div>
        }
        summary={[
          { label: "Activity", value: sports.length === 0 ? "All" : sports.join(", "),
            onClear: sports.length > 0 ? () => toggleSport([]) : undefined },
          ...(technique !== "all"
            ? [{ label: "Technique", value: techniqueLabel(technique),
                onClear: () => setListParam("technique", null, false) }]
            : []),
          { label: "Status", value: status === "all" ? "All" : status,
            onClear: status !== "all" ? () => setListParam("status", null, false) : undefined },
          { label: "Score", value: scoreSummary,
            onClear: scoreMode !== "all" ? () => setScoreModeAndReset("all") : undefined },
          { label: "Tier",
            value: tiers.length === 0 ? "All" : tiers.map((t) => TIER_LABELS[t] ?? t).join(", "),
            onClear: tiers.length > 0 ? clearTiers : undefined },
          ...(athletes.length > 0
            ? [{ label: "Athlete", value: athletes.map((a) => a.name).join(", "),
                onClear: () => setAthletesAndReset([]) }]
            : []),
        ]}
      >
        <ActivityMultiFilter values={sports} onChange={toggleSport} />
        {techniqueOptions.length > 0 && (
          <FilterRow label="Technique / angle">
            <FilterChip
              active={technique === "all"}
              onClick={() => setListParam("technique", null, false)}
              icon={<Globe2 className="h-4 w-4" />}
              label="All"
            />
            {techniqueOptions.map((t) => (
              <FilterChip
                key={t.id}
                active={technique === t.id}
                onClick={() => setListParam("technique", t.id, false)}
                emoji={t.emoji}
                label={t.label.replace(/^[^—]+ — /, "")}
              />
            ))}
          </FilterRow>
        )}
        <OptionFilter
          label="Status"
          options={STATUSES}
          value={status}
          onChange={(v) => setListParam("status", v === "all" ? null : v, false)}
        />
        <FilterRow label="Outcome score" hint="filters on the server — totals update too">
          <FilterChip
            active={scoreMode === "all"}
            onClick={() => setScoreModeAndReset("all")}
            icon={<Globe2 className="h-4 w-4" />}
            label="All scores"
          />
          <FilterChip
            active={scoreMode === "none"}
            onClick={() => setScoreModeAndReset("none")}
            icon={<CircleSlash className="h-4 w-4" />}
            label="No score"
          />
          <FilterChip
            active={scoreMode === "range"}
            onClick={() => setScoreModeAndReset("range")}
            icon={<Gauge className="h-4 w-4" />}
            label="Score range"
          />
        </FilterRow>
        {scoreMode === "range" && (
          <ScoreRange min={scoreMin} max={scoreMax} onChange={setScoreRangeAndReset} />
        )}
        <TierFilterRow
          tiers={tiers}
          onToggle={toggleTier}
          onClear={clearTiers}
          hint="the clip's tier tag — pick several, filters on the server"
        />
        <AthleteFilterRow
          athletes={athletes}
          onAdd={addAthlete}
          onRemove={removeAthlete}
          label="Athlete in video"
          hint="who's swinging — not who uploaded; pick several"
        />
      </FilterCard>

      <div className="flex items-center justify-between gap-3 px-1">
        <div className="text-xs text-slate-500">
          {isLoading
            ? "Loading sessions…"
            : error
            ? <span className="text-rose-600">Couldn't load sessions. <button onClick={reload} className="underline">Retry</button></span>
            : <>{filtered.length} of {sessions.length} loaded{totalCount != null ? ` · ${totalCount} total` : ""} · latest first</>}
        </div>
        <button
          type="button"
          onClick={() => onRescore(Array.from(selectedIds))}
          disabled={selectedIds.size === 0 || rescoring}
          title="Queue a fresh scoring render for every selected session"
          className="btn-secondary !h-9"
        >
          {rescoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Re-score selected{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
        </button>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[680px]">
          <thead>
            <tr className="text-left text-[0.7rem] uppercase tracking-wide text-slate-500 bg-slate-50/60 border-b border-slate-100">
              <th className="pl-5 pr-1 py-3 font-semibold w-8">
                <input
                  type="checkbox"
                  aria-label="Select all sessions"
                  className="h-4 w-4 rounded border-slate-300 accent-el-600"
                  checked={filtered.length > 0 && filtered.every((s) => selectedIds.has(s.id))}
                  onChange={(e) =>
                    setSelectedIds(e.target.checked ? new Set(filtered.map((s) => s.id)) : new Set())
                  }
                />
              </th>
              <th className="px-5 py-3 font-semibold">Capture</th>
              {/* Athlete = who is IN the video (subject tag, uploader fallback) — leads
                  Person (the uploader) per user direction 2026-08-27. */}
              <th className="px-5 py-3 font-semibold">Athlete</th>
              <th className="px-5 py-3 font-semibold">Person</th>
              <th className="px-5 py-3 font-semibold">Movement / task</th>
              <th className="px-5 py-3 font-semibold">Captured</th>
              <th className="px-5 py-3 font-semibold">Outcome</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && sessions.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-5 py-16">
                  <div className="flex flex-col items-center gap-2 text-slate-500">
                    <Loader2 className="h-6 w-6 animate-spin text-el-600" />
                    <div className="text-sm">Loading sessions…</div>
                  </div>
                </td>
              </tr>
            ) : (
              <>
                {filtered.map((s) => (
                  <Row
                    key={s.id}
                    s={s}
                    onDelete={onDelete}
                    deleting={deletingId === s.id}
                    selected={selectedIds.has(s.id)}
                    onToggleSelect={toggleSelected}
                    queued={queuedIds.has(s.id) || !!s.rescorePending}
                    onRescore={() => onRescore([s.id])}
                    rescoring={rescoring}
                  />
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-5 py-12 text-center text-sm text-slate-500">
                      No sessions match your filters.
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
        </div>
      </div>

      {hasMore && (
        <div className="flex justify-center pt-1">
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={isLoading}
            className="btn-secondary !h-9"
          >
            {isLoading ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Loading…</>
            ) : (
              <>Load more · {sessions.length} of {totalCount}</>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

function Row({
  s,
  onDelete,
  deleting,
  selected,
  onToggleSelect,
  queued,
  onRescore,
  rescoring,
}: {
  s: Session;
  onDelete: (s: Session) => void;
  deleting: boolean;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  queued: boolean;
  onRescore: () => void;
  rescoring: boolean;
}) {
  // Outcome framing comes from the session itself, not the org. Risk-tagged
  // captures show a RiskBadge; performance captures show a quality bar.
  const isRisk = !!s.risk;

  return (
    <tr className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
      <td className="pl-5 pr-1 py-3">
        <input
          type="checkbox"
          aria-label={`Select session ${s.id}`}
          className="h-4 w-4 rounded border-slate-300 accent-el-600"
          checked={selected}
          onChange={() => onToggleSelect(s.id)}
        />
      </td>
      <td className="px-5 py-3">
        <Link to={`/sessions/${s.id}`} className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center text-lg">
            {s.thumb}
          </div>
          <div className="leading-tight">
            <div className="text-sm font-medium text-slate-900">{s.id}</div>
            <div className="text-[0.7rem] text-slate-500">tap to open</div>
          </div>
        </Link>
      </td>
      <td className="px-5 py-3">
        {s.subjectContactId != null ? (
          <Link
            to={`/athletes/${s.subjectContactId}`}
            className="text-slate-800 font-medium hover:text-el-700"
          >
            {s.subjectName ?? s.athlete}
          </Link>
        ) : (
          <span className="text-slate-800">{s.subjectName ?? s.athlete}</span>
        )}
      </td>
      <td className="px-5 py-3">
        <Link to={`/roster/${s.athleteId}`} className="text-slate-800 hover:text-el-700">
          {s.athlete}
        </Link>
      </td>
      <td className="px-5 py-3 text-slate-600">
        {s.task ?? ACTIVITY_LABEL[s.activity]}
      </td>
      <td className="px-5 py-3 text-slate-600">{s.capturedAt ? formatTime(s.capturedAt) : "—"}</td>
      <td className="px-5 py-3">
        {isRisk ? (
          <RiskBadge risk={s.risk!} />
        ) : s.status === "processed" && s.score != null ? (
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-20 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-el-400 to-el-600"
                style={{ width: `${s.score}%` }}
              />
            </div>
            <span className="text-xs text-slate-700 font-medium">{s.score}</span>
          </div>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        )}
      </td>
      <td className="px-5 py-3">
        <div className="flex flex-col items-start gap-1">
          <StatusBadge status={s.status} />
          {queued && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[0.65rem] font-medium text-amber-700">
              <RefreshCw className="h-3 w-3" /> Re-score queued
            </span>
          )}
        </div>
      </td>
      <td className="px-5 py-3 text-right">
        <button
          type="button"
          onClick={onRescore}
          disabled={queued || rescoring}
          title="Queue a fresh scoring render for this session"
          aria-label="Re-score session"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition hover:bg-el-50 hover:text-el-700 disabled:opacity-50"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onDelete(s)}
          disabled={deleting}
          title="Delete session and all of its data"
          aria-label="Delete session"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
        >
          {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
        </button>
      </td>
    </tr>
  );
}

